// ===== 七牛云 Kodo 备份客户端（浏览器端直传）=====
// 架构：前端请求 Supabase Edge Function 获取 uploadToken → 浏览器直传七牛 → 上传成功返回。
// SK 永不出现在前端，只有短时效 token 流经浏览器。
//
// 与 webdav.ts / aliyunpan.ts 同级，由 backupService.ts 编排。

/** 七牛备份配置（存 localStorage changfa_qiniu_cfg） */
export interface QiniuConfig {
  /** Supabase Edge Function URL，如 https://xxxx.supabase.co/functions/v1/qiniu-backup */
  edgeFunctionUrl: string;
  /** 七牛存储空间名 */
  bucket: string;
  /** 区域：z0(华东) | z1(华北) | z2(华南) | na0(北美) | as0(东南亚) */
  region: string;
}

/** 七牛文件信息（list 返回） */
export interface QiniuFile {
  key: string;
  fsize: number;
  putTime: number; // 七牛 100 纳秒级时间戳
  mimeType: string;
}

export interface QiniuTestResult {
  ok: boolean;
  message: string;
}

/** 七牛 putTime（100 纳秒级）转 ISO 字符串 */
export function qiniuPutTimeToISO(putTime: number): string {
  // putTime 是 100 纳秒级，需 / 1e7 得到秒
  return new Date(putTime / 1e7 * 1000).toISOString();
}

/** 快速格式化文件大小 */
export function fmtQiniuSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// ===== Edge Function 通信 =====

/** 调用 Edge Function，返回带类型的结果 */
async function callEdgeFunction<T extends Record<string, unknown>>(
  cfg: QiniuConfig,
  payload: Record<string, unknown>,
): Promise<{ ok: boolean; message?: string } & T> {
  const url = cfg.edgeFunctionUrl.replace(/\/+$/, '');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  try {
    return JSON.parse(text) as { ok: boolean; message?: string } & T;
  } catch {
    return { ok: false, message: `Edge Function 返回非 JSON（HTTP ${res.status}）` } as { ok: boolean; message?: string } & T;
  }
}

// ===== 测试连接 =====

/**
 * 测试连接：向 Edge Function 请求一个临时 token，验证 AK/SK/Bucket 配置是否正确。
 * 不实际上传文件，只验证签名服务可用。
 */
export async function testQiniuConnection(cfg: QiniuConfig): Promise<QiniuTestResult> {
  if (!cfg.edgeFunctionUrl?.trim()) {
    return { ok: false, message: '请填写 Edge Function URL' };
  }
  if (!cfg.bucket?.trim()) {
    return { ok: false, message: '请填写七牛存储空间名（bucket）' };
  }
  try {
    // 用一个探针 key 测试签名服务是否可用
    const res = await callEdgeFunction<{ uploadToken?: string }>(cfg, {
      action: 'upload-token',
      key: '__changfa_qiniu_probe__.json',
    });
    if (res.ok && res.uploadToken) {
      return { ok: true, message: '签名服务连接成功，AK/SK/Bucket 配置正确' };
    }
    return { ok: false, message: res.message ?? '签名服务返回异常' };
  } catch (e) {
    const msg = e instanceof Error ? e.message : '网络错误';
    if (msg === 'Failed to fetch') {
      return { ok: false, message: '连接失败：无法访问 Edge Function URL，请检查地址是否正确' };
    }
    return { ok: false, message: `连接失败：${msg}` };
  }
}

// ===== 上传 =====

/**
 * 上传备份 JSON 到七牛 Kodo。
 * 流程：请求 Edge Function 获取 uploadToken → multipart/form-data 直传七牛上传服务器。
 *
 * @param cfg    七牛配置
 * @param key    远程文件名（如「昌发工作台备份_xxx.json」）
 * @param content  JSON 字符串
 */
export async function qiniuUpload(
  cfg: QiniuConfig,
  key: string,
  content: string,
): Promise<{ ok: boolean; message: string; status?: number }> {
  // 1) 获取 uploadToken
  const tokenRes = await callEdgeFunction<{ uploadToken?: string; uploadUrl?: string }>(cfg, {
    action: 'upload-token',
    key,
  });
  if (!tokenRes.ok || !tokenRes.uploadToken || !tokenRes.uploadUrl) {
    return { ok: false, message: `获取上传凭证失败：${tokenRes.message ?? '未知错误'}` };
  }

  // 2) multipart/form-data 直传七牛
  const formData = new FormData();
  formData.append('token', tokenRes.uploadToken);
  formData.append('key', key);
  formData.append('file', new Blob([content], { type: 'application/json' }), key);

  try {
    const res = await fetch(tokenRes.uploadUrl, {
      method: 'POST',
      body: formData,
    });
    if (res.ok) {
      return { ok: true, message: `已上传：${key}` };
    }
    // 解析七牛错误响应
    let detail = '';
    try {
      const j = (await res.json()) as { error?: string };
      detail = j?.error ?? '';
    } catch { /* Best-effort operation; failure is non-fatal. */ }
    return { ok: false, status: res.status, message: detail || `上传失败（HTTP ${res.status}）` };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return { ok: false, message: '上传超时，备份包较大或网络不稳定' };
    }
    return { ok: false, message: `上传失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
}

// ===== 列举文件 =====

/**
 * 列举七牛存储空间中的备份文件。
 * 返回 { ok, files, marker } —— marker 非空表示还有更多文件可翻页。
 */
export async function qiniuList(
  cfg: QiniuConfig,
  prefix = '',
  limit = 50,
  marker = '',
): Promise<{ ok: boolean; files: QiniuFile[]; marker: string; message?: string }> {
  try {
    const res = await callEdgeFunction<{ items?: QiniuFile[]; marker?: string }>(cfg, {
      action: 'list',
      prefix,
      limit,
      marker,
    });
    if (res.ok) {
      return { ok: true, files: res.items ?? [], marker: res.marker ?? '' };
    }
    return { ok: false, files: [], marker: '', message: res.message ?? '列举失败' };
  } catch (e) {
    return {
      ok: false,
      files: [],
      marker: '',
      message: `列举失败：${e instanceof Error ? e.message : '网络错误'}`,
    };
  }
}

// ===== 下载 =====

/**
 * 获取签名下载 URL（1 小时有效），浏览器可直接 fetch 下载文件内容。
 */
export async function qiniuDownloadUrl(
  cfg: QiniuConfig,
  key: string,
): Promise<{ ok: boolean; url?: string; message?: string }> {
  try {
    const res = await callEdgeFunction<{ url?: string }>(cfg, {
      action: 'download-url',
      key,
    });
    if (res.ok && res.url) {
      return { ok: true, url: res.url };
    }
    return { ok: false, message: res.message ?? '获取下载链接失败' };
  } catch (e) {
    return { ok: false, message: `获取下载链接失败：${e instanceof Error ? e.message : '网络错误'}` };
  }
}

/**
 * 下载并返回备份文件文本内容（用于恢复流程）。
 */
export async function qiniuDownloadText(
  cfg: QiniuConfig,
  key: string,
): Promise<{ ok: boolean; text?: string; message?: string }> {
  const urlRes = await qiniuDownloadUrl(cfg, key);
  if (!urlRes.ok || !urlRes.url) {
    return { ok: false, message: urlRes.message ?? '获取下载链接失败' };
  }
  try {
    const res = await fetch(urlRes.url);
    if (!res.ok) {
      return { ok: false, message: `下载失败（HTTP ${res.status}）` };
    }
    const text = await res.text();
    return { ok: true, text };
  } catch (e) {
    return { ok: false, message: `下载失败：${e instanceof Error ? e.message : '网络错误'}` };
  }
}
